import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { formatINR, formatDate, cplHealth, statusColor } from "../utils/format";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, Users, Target, Wallet, Globe, Bell,
  CheckSquare, Sparkles, Plus, Inbox, RefreshCcw, Search, X,
  FileSpreadsheet, FileText, UserPlus, PhoneIncoming, Phone,
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import StructuredInsightsPanel from "../components/StructuredInsightsPanel";
import { useGlobalDate } from "../contexts/GlobalDateContext";

const TABS = [
  { id: "overview", label: "Overview", icon: Building2 },
  { id: "campaigns", label: "Campaigns", icon: Target },
  { id: "budget", label: "Budget", icon: Wallet },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "landing", label: "Landing Pages", icon: Globe },
  { id: "alerts", label: "Alerts", icon: Bell },
  { id: "tasks", label: "Tasks", icon: CheckSquare },
  { id: "ai", label: "AI Insights", icon: Sparkles },
];

const SOURCE_META = {
  google_ads_call_lead: { label: "Phone call · Google Ads", Icon: PhoneIncoming },
  google_sheets:        { label: "Google Sheets",           Icon: FileSpreadsheet },
  csv_upload:           { label: "CSV upload",              Icon: FileText },
  manual:               { label: "Manual",                  Icon: UserPlus },
  website:              { label: "Website",                 Icon: Globe },
  seed:                 { label: "Demo",                    Icon: FileText },
};

const LEAD_TYPE_OPTIONS = [
  { key: "search",   label: "Search" },
  { key: "pmax",     label: "PMAX" },
  { key: "gbp",      label: "GBP / Local" },
  { key: "boosting", label: "Boosting / Social" },
  { key: "other",    label: "Other" },
];

export default function ClientWorkspace() {
  const { id } = useParams();
  const { range: dateRange, customFrom, customTo, label: rangeLabel } = useGlobalDate();
  const [ws, setWs] = useState(null);
  const [tab, setTab] = useState("overview");

  // Leads tab state — filters mirror /leads page
  const [leads, setLeads] = useState([]);
  const [leadsTotal, setLeadsTotal] = useState(0);
  const [leadsPage, setLeadsPage] = useState(1);
  const [leadsPageSize, setLeadsPageSize] = useState(50);
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [filterValues, setFilterValues] = useState({ sources: [], cities: [] });
  const [searchQ, setSearchQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [campaignFilter, setCampaignFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [leadTypeFilter, setLeadTypeFilter] = useState("");
  const [officialOnly, setOfficialOnly] = useState(false);
  const [qualifiedOnly, setQualifiedOnly] = useState(false);
  const [whatsappOnly, setWhatsappOnly] = useState(false);
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);

  const [newTask, setNewTask] = useState(null);
  const [users, setUsers] = useState([]);
  const [syncing, setSyncing] = useState(false);

  // ----- Workspace bundle (tiles + chart) — refetch on date-range change -----
  const load = async () => {
    try {
      const params = {};
      if (dateRange === "custom") {
        if (customFrom) params.date_from = customFrom;
        if (customTo) params.date_to = customTo;
        params.date_range = "custom";
      } else if (dateRange && dateRange !== "all") {
        params.date_range = dateRange;
      }
      const { data } = await api.get(`/clients/${id}/workspace`, { params });
      setWs(data);
    } catch {
      toast.error("Failed to load client workspace");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, dateRange, customFrom, customTo]);

  // ----- Leads tab — re-fetch on filter / page / range change -----
  const buildLeadsParams = () => {
    const p = {
      client_id: id,
      client_scope: true,
      limit: leadsPageSize,
      offset: (leadsPage - 1) * leadsPageSize,
    };
    if (searchQ.trim()) p.q = searchQ.trim();
    if (statusFilter) p.status = statusFilter;
    if (campaignFilter) p.campaign_id = campaignFilter;
    if (sourceFilter) p.source = sourceFilter;
    if (cityFilter) p.city = cityFilter;
    if (leadTypeFilter) p.lead_type = leadTypeFilter;
    if (officialOnly) p.official_only = true;
    if (qualifiedOnly) p.qualified_calls_only = true;
    if (whatsappOnly) p.whatsapp_only = true;
    if (duplicatesOnly) p.duplicates_only = true;
    if (dateRange === "custom") {
      if (customFrom) p.date_from = customFrom;
      if (customTo) p.date_to = customTo;
    } else if (dateRange && dateRange !== "all") {
      p.date_range = dateRange;
    }
    return p;
  };

  useEffect(() => {
    if (tab !== "leads") return;
    api.get("/leads", { params: buildLeadsParams() }).then((r) => {
      setLeads(r.data.leads || []);
      setLeadsTotal(r.data.total || 0);
    }).catch(() => { setLeads([]); setLeadsTotal(0); });
    // eslint-disable-next-line
  }, [
    tab, id, leadsPage, leadsPageSize,
    searchQ, statusFilter, campaignFilter, sourceFilter, cityFilter,
    leadTypeFilter, officialOnly, qualifiedOnly, whatsappOnly, duplicatesOnly,
    dateRange, customFrom, customTo,
  ]);

  // Reset page-1 when entering tab or any filter changes
  useEffect(() => {
    setLeadsPage(1);
  }, [
    tab, id,
    searchQ, statusFilter, campaignFilter, sourceFilter, cityFilter,
    leadTypeFilter, officialOnly, qualifiedOnly, whatsappOnly, duplicatesOnly,
    dateRange, customFrom, customTo, leadsPageSize,
  ]);

  useEffect(() => {
    api.get("/admin/lead-statuses").then((r) => setLeadStatuses(r.data.statuses || []));
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/leads/filter-values/all").then((r) => setFilterValues(r.data)).catch(() => {});
  }, []);

  const syncNow = async () => {
    if (syncing) return;
    setSyncing(true);
    const t = toast.loading("Syncing client data…");
    try {
      const { data } = await api.post(`/clients/${id}/sync-now`);
      const t1 = data.totals || {};
      const ok = data.ads_accounts_synced || 0;
      const fail = data.ads_accounts_failed || 0;
      const sheets = data.sheets_mappings_synced || 0;
      const parts = [];
      if (ok || fail) parts.push(`${ok} Ads acct${ok === 1 ? "" : "s"}${fail ? ` (${fail} failed)` : ""}`);
      if (sheets) parts.push(`${sheets} Sheets mapping${sheets === 1 ? "" : "s"}`);
      if (t1.campaigns_inserted || t1.campaigns_updated) parts.push(`camp +${t1.campaigns_inserted}~${t1.campaigns_updated}`);
      if (t1.call_leads_inserted) parts.push(`+${t1.call_leads_inserted} call leads`);
      if (t1.sheets_inserted) parts.push(`+${t1.sheets_inserted} sheet leads`);
      toast.success(parts.length ? `Synced · ${parts.join(" · ")}` : "Sync complete", { id: t });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed", { id: t });
    } finally {
      setSyncing(false);
    }
  };

  const resetFilters = () => {
    setSearchQ(""); setStatusFilter(""); setCampaignFilter(""); setSourceFilter("");
    setCityFilter(""); setLeadTypeFilter(""); setOfficialOnly(false);
    setQualifiedOnly(false); setWhatsappOnly(false); setDuplicatesOnly(false);
  };

  const activeFilterCount = useMemo(() => [
    searchQ.trim(), statusFilter, campaignFilter, sourceFilter, cityFilter, leadTypeFilter,
    officialOnly ? "off" : "", qualifiedOnly ? "qc" : "", whatsappOnly ? "wa" : "",
    duplicatesOnly ? "dup" : "",
  ].filter(Boolean).length, [
    searchQ, statusFilter, campaignFilter, sourceFilter, cityFilter, leadTypeFilter,
    officialOnly, qualifiedOnly, whatsappOnly, duplicatesOnly,
  ]);

  if (!ws) return <Layout title="Loading…"><div className="text-sm text-muted-foreground p-6">Loading workspace…</div></Layout>;

  const s = ws?.summary || {};
  const activeCamps = ws.campaigns.filter((c) => c.status === "active");
  const unresolvedAlerts = ws.alerts.filter((a) => a.status !== "resolved");
  const openTasks = ws.tasks.filter((t) => t.status !== "done");
  const sources = filterValues.sources?.length ? filterValues.sources : Object.keys(SOURCE_META);

  // Tile range label — "Leads · 7d" / "Leads · 30d" / "Leads · 1y" / "Leads · Today"
  const tileSub = s.range_label || "7d";

  return (
    <Layout title={ws.name} subtitle={`${ws.niche || "—"} · ${ws.region || "—"}`}>
      <div className="flex items-center justify-between mb-4">
        <Link to="/clients" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-clients">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Clients
        </Link>
        <div className="flex items-center gap-2">
          <span
            className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 text-xs border border-border rounded-sm bg-secondary/40"
            title="Driven by the global top-bar date selector"
          >
            <span className="text-muted-foreground">📅</span>
            <span className="font-medium">{rangeLabel}</span>
            {s.range_from && (
              <span className="text-muted-foreground text-[11px] hidden lg:inline">
                · {s.range_from}{s.range_from !== s.range_to ? ` → ${s.range_to}` : ""}
              </span>
            )}
          </span>
          <button
            onClick={syncNow}
            disabled={syncing}
            data-testid="client-sync-now-btn"
            className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-60"
          >
            <RefreshCcw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>
      </div>

      {/* Summary strip — labels reflect the picked range */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-6" data-testid="ws-summary-tiles">
        <Metric label="Campaigns" value={`${s.active_campaigns}/${s.total_campaigns}`} sub="Active / Total" />
        <Metric label={`Spend · ${tileSub}`} value={formatINR(s.spend_primary ?? s.spend_7d, { compact: true })} />
        <Metric label={`Leads · ${tileSub}`} value={s.leads_primary ?? s.leads_7d} />
        <Metric label={`CPL · ${tileSub}`} value={(s.cpl_primary ?? s.cpl_7d) ? formatINR(s.cpl_primary ?? s.cpl_7d) : "—"} tone={cplHealth(s.cpl_primary ?? s.cpl_7d)} />
        <Metric label="Monthly Budget" value={formatINR(s.monthly_budget, { compact: true })} />
        <Metric label="Critical Alerts" value={s.critical_alerts} tone={s.critical_alerts ? "critical" : "success"} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto" data-testid="workspace-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`tab-${t.id}`}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap ${
                tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.75} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-3">Trend · {tileSub}</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={ws.trend_30d}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(220 15% 90%)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid hsl(220 15% 90%)", fontSize: 12 }} />
                <Line yAxisId="l" type="monotone" dataKey="spend" stroke="hsl(214 100% 35%)" strokeWidth={2} dot={false} name="Spend ₹" />
                <Line yAxisId="r" type="monotone" dataKey="leads" stroke="hsl(150 70% 30%)" strokeWidth={2} dot={false} name="Leads" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card border border-border rounded-sm p-5">
              <div className="label-mono mb-3">Google Ads Accounts</div>
              {ws.accounts.length === 0 && <div className="text-sm text-muted-foreground">No accounts linked.</div>}
              {ws.accounts.map((a) => (
                <Link key={a.id} to={`/accounts/${a.id}`} className="flex items-center justify-between py-2 border-b border-border last:border-b-0 hover:bg-secondary/30 -mx-2 px-2 rounded-sm">
                  <div>
                    <div className="text-sm font-medium">{a.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{a.google_customer_id}</div>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-sm ${a.active ? "bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" : "bg-secondary"}`}>
                    {a.active ? "Active" : "Inactive"}
                  </span>
                </Link>
              ))}
            </div>
            <div className="bg-card border border-border rounded-sm p-5">
              <div className="label-mono mb-3">Open Tasks ({openTasks.length})</div>
              {openTasks.slice(0, 6).map((t) => (
                <div key={t.id} className="py-2 border-b border-border last:border-b-0">
                  <div className="text-sm font-medium">{t.title}</div>
                  <div className="text-[11px] text-muted-foreground">{t.priority} · {t.due_date ? formatDate(t.due_date) : "no due"}</div>
                </div>
              ))}
              {openTasks.length === 0 && <div className="text-sm text-muted-foreground">No open tasks.</div>}
            </div>
          </div>
        </div>
      )}

      {/* CAMPAIGNS */}
      {tab === "campaigns" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full data-table" data-testid="client-campaigns-table">
            <thead><tr><th>Campaign</th><th>Type</th><th>Status</th><th className="text-right">Daily ₹</th><th className="text-right">Monthly ₹</th></tr></thead>
            <tbody>
              {ws.campaigns.map((c) => (
                <tr key={c.id}>
                  <td><Link to={`/campaigns/${c.id}`} className="font-medium hover:text-primary">{c.name}</Link></td>
                  <td>{c.type}</td>
                  <td><StatusBadge tone={statusColor(c.status)}>{c.status}</StatusBadge></td>
                  <td className="text-right">{formatINR(c.daily_budget)}</td>
                  <td className="text-right">{formatINR(c.monthly_budget)}</td>
                </tr>
              ))}
              {ws.campaigns.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-sm text-muted-foreground">No campaigns yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* BUDGET */}
      {tab === "budget" && (
        <div className="bg-card border border-border rounded-sm overflow-hidden">
          <table className="w-full data-table">
            <thead><tr><th>Campaign</th><th className="text-right">Daily</th><th className="text-right">Monthly</th><th>Status</th></tr></thead>
            <tbody>
              {ws.campaigns.map((c) => (
                <tr key={c.id}>
                  <td><Link to={`/campaigns/${c.id}`} className="font-medium hover:text-primary">{c.name}</Link></td>
                  <td className="text-right">{formatINR(c.daily_budget)}</td>
                  <td className="text-right">{formatINR(c.monthly_budget)}</td>
                  <td><StatusBadge tone={statusColor(c.status)}>{c.status}</StatusBadge></td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="p-4 border-t border-border flex justify-between text-sm">
            <span className="text-muted-foreground">Total Monthly Budget</span>
            <span className="font-semibold">{formatINR(s.monthly_budget)}</span>
          </div>
        </div>
      )}

      {/* LEADS — full filter bar matching /leads page */}
      {tab === "leads" && (
        <div className="space-y-3" data-testid="client-leads-tab">
          {/* Search */}
          <div className="relative" data-testid="ws-leads-search-bar">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.75} />
            <input
              data-testid="ws-leads-search-input"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search by name, phone, campaign or city…"
              className="w-full h-10 pl-9 pr-9 border border-border bg-card rounded-sm text-sm focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
            />
            {searchQ && (
              <button onClick={() => setSearchQ("")} className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="ws-leads-search-clear">
                <X className="w-3.5 h-3.5 text-muted-foreground hover:text-foreground" />
              </button>
            )}
          </div>

          {/* Filter bar */}
          <div className="bg-card border border-border rounded-sm p-3" data-testid="ws-leads-filter-bar">
            <div className="flex flex-wrap items-center gap-2">
              {/* Date range — read-only, set from top-bar */}
              <div
                className="h-9 px-3 inline-flex items-center gap-1.5 border border-border rounded-sm text-xs bg-secondary/40"
                title="Date range is set from the top-bar — applies across all pages"
                data-testid="ws-leads-daterange-chip"
              >
                <span className="text-muted-foreground">📅</span>
                <span className="font-medium">{dateRange === "custom" ? `${customFrom || "—"} → ${customTo || "—"}` : rangeLabel}</span>
              </div>

              <select
                value={campaignFilter}
                onChange={(e) => setCampaignFilter(e.target.value)}
                data-testid="ws-leads-filter-campaign"
                className="h-9 px-3 border border-border rounded-sm text-sm min-w-[160px]"
              >
                <option value="">All campaigns</option>
                {ws.campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>

              <select
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                data-testid="ws-leads-filter-source"
                className="h-9 px-3 border border-border rounded-sm text-sm"
              >
                <option value="">All sources</option>
                {sources.map((src) => <option key={src} value={src}>{SOURCE_META[src]?.label || src}</option>)}
              </select>

              <select
                value={leadTypeFilter}
                onChange={(e) => setLeadTypeFilter(e.target.value)}
                data-testid="ws-leads-filter-leadtype"
                className="h-9 px-3 border border-border rounded-sm text-sm"
                title="Channel / Lead type — derived from campaign name (Search / PMAX / GBP / Boosting / Other)"
              >
                <option value="">All channels</option>
                {LEAD_TYPE_OPTIONS.map((lt) => <option key={lt.key} value={lt.key}>{lt.label}</option>)}
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                data-testid="ws-leads-filter-status"
                className="h-9 px-3 border border-border rounded-sm text-sm"
              >
                <option value="">All statuses</option>
                {leadStatuses.map((s2) => <option key={s2.key} value={s2.key}>{s2.label || s2.key}</option>)}
              </select>

              <select
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                data-testid="ws-leads-filter-city"
                className="h-9 px-3 border border-border rounded-sm text-sm"
              >
                <option value="">All cities</option>
                {(filterValues.cities || []).slice(0, 200).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>

              <Toggle
                checked={officialOnly}
                onChange={setOfficialOnly}
                label="Official only"
                tip="Marketing-team rule: only Search Ads leads count as official. PMAX, GBP and Boosting are excluded from this set."
                testid="ws-leads-toggle-official"
              />
              <Toggle
                checked={qualifiedOnly}
                onChange={setQualifiedOnly}
                label="Qualified calls"
                tip="Call leads with duration > 15 seconds. Empty until your sheet maps a 'Call duration' column."
                testid="ws-leads-toggle-qualified"
              />
              <Toggle
                checked={whatsappOnly}
                onChange={setWhatsappOnly}
                label="WhatsApp clicks"
                tip="Leads with whatsapp_click=true."
                testid="ws-leads-toggle-whatsapp"
              />
              <Toggle
                checked={duplicatesOnly}
                onChange={setDuplicatesOnly}
                label="Duplicates"
                tip="Leads marked status=duplicate by the dedupe engine."
                testid="ws-leads-toggle-duplicates"
              />

              {activeFilterCount > 0 && (
                <button
                  onClick={resetFilters}
                  data-testid="ws-leads-reset-filters"
                  className="h-9 px-3 text-xs font-medium border border-border rounded-sm hover:bg-secondary text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5"
                >
                  <X className="w-3.5 h-3.5" /> Reset ({activeFilterCount})
                </button>
              )}

              <div className="ml-auto text-xs text-muted-foreground">
                <span className="font-semibold tabular text-foreground">{leadsTotal}</span> matching
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="bg-card border border-border rounded-sm overflow-hidden" data-testid="client-leads-table">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th>Name</th><th>Phone</th><th>City</th>
                  <th title="Sorted by lead_date DESC (canonical sheet date)">Date ↓</th>
                  <th>Channel</th><th>Status</th><th>Campaign</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className={l.looks_like_header ? "opacity-60 bg-[hsl(38_92%_50%)]/10" : ""}>
                    <td className="font-medium">
                      {l.looks_like_header && <span title="This row looks like a sheet header — flagged by v1.4.1; admin can clean it up from Connectors." className="mr-1.5 text-[hsl(38_92%_45%)] text-[10px] uppercase font-mono">⚠ HDR</span>}
                      {l.name || "—"}
                    </td>
                    <td className="font-mono text-xs">{l.phone}</td>
                    <td>{l.city}</td>
                    <td>{l.lead_date || l.date ? formatDate(l.lead_date || l.date) : <span className="text-muted-foreground italic">unknown</span>}</td>
                    <td className="text-xs uppercase tracking-wide text-muted-foreground">{l.lead_type || "—"}</td>
                    <td><StatusBadge tone={leadStatuses.find(s2 => s2.key === l.status)?.color || "primary"}>{l.status}</StatusBadge></td>
                    <td className="text-xs text-muted-foreground">{ws.campaigns.find(c => c.id === l.campaign_id)?.name || "—"}</td>
                  </tr>
                ))}
                {leads.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-sm text-muted-foreground">No leads match the current filters.</td></tr>}
              </tbody>
            </table>
            {leadsTotal > 0 && (
              <div className="px-4 py-3 border-t border-border flex flex-wrap items-center justify-between gap-3" data-testid="ws-leads-pagination">
                <div className="text-xs text-muted-foreground">
                  Showing <span className="font-semibold tabular text-foreground">{(leadsPage - 1) * leadsPageSize + 1}</span>
                  {" – "}
                  <span className="font-semibold tabular text-foreground">{Math.min(leadsPage * leadsPageSize, leadsTotal)}</span>
                  {" of "}
                  <span className="font-semibold tabular text-foreground">{leadsTotal}</span>
                </div>
                <div className="flex items-center gap-2">
                  <select value={leadsPageSize} onChange={(e) => { setLeadsPageSize(parseInt(e.target.value, 10)); setLeadsPage(1); }} data-testid="ws-leads-page-size" className="h-8 px-2 border border-border rounded-sm text-xs bg-card">
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <button onClick={() => setLeadsPage((p) => Math.max(1, p - 1))} disabled={leadsPage === 1} data-testid="ws-leads-page-prev" className="h-8 px-3 text-xs border border-border rounded-sm hover:bg-secondary disabled:opacity-40">← Prev</button>
                  <span className="text-xs tabular px-2">Page <span className="font-semibold">{leadsPage}</span> / {Math.max(1, Math.ceil(leadsTotal / leadsPageSize))}</span>
                  <button onClick={() => setLeadsPage((p) => p + 1)} disabled={leadsPage * leadsPageSize >= leadsTotal} data-testid="ws-leads-page-next" className="h-8 px-3 text-xs border border-border rounded-sm hover:bg-secondary disabled:opacity-40">Next →</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* LANDING PAGES */}
      {tab === "landing" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {ws.landing_pages.map((lp) => (
            <Link key={lp.id} to={`/landing-pages/${lp.id}`} className="bg-card border border-border rounded-sm p-5 hover:border-primary/50">
              <div className="font-heading font-medium">{lp.title}</div>
              <div className="text-xs text-primary truncate mt-1">{lp.url}</div>
              <div className="text-xs text-muted-foreground mt-2">Health: {lp.health_score}/100</div>
            </Link>
          ))}
          {ws.landing_pages.length === 0 && <div className="text-sm text-muted-foreground col-span-2 p-6 text-center">No landing pages.</div>}
        </div>
      )}

      {/* ALERTS */}
      {tab === "alerts" && (
        <div className="bg-card border border-border rounded-sm">
          {unresolvedAlerts.map((a) => (
            <div key={a.id} className="flex items-start gap-4 p-4 border-b border-border last:border-b-0">
              <StatusBadge tone={a.severity === "critical" ? "critical" : a.severity === "warning" ? "warning" : "primary"}>{a.severity}</StatusBadge>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{a.title}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{a.description}</div>
              </div>
              <StatusBadge tone="muted">{a.status}</StatusBadge>
            </div>
          ))}
          {unresolvedAlerts.length === 0 && <div className="p-6 text-center text-sm text-muted-foreground">No active alerts.</div>}
        </div>
      )}

      {/* TASKS */}
      {tab === "tasks" && (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={() => setNewTask({ title: "", description: "", priority: "medium", linked_client_id: id, assignee_user_id: "", due_date: "" })} data-testid="ws-new-task-btn" className="h-9 px-3 bg-primary text-primary-foreground text-xs font-medium rounded-sm flex items-center gap-1.5"><Plus className="w-3.5 h-3.5" /> New Task</button>
          </div>
          <div className="bg-card border border-border rounded-sm overflow-hidden">
            <table className="w-full data-table">
              <thead><tr><th>Task</th><th>Priority</th><th>Assignee</th><th>Due</th><th>Status</th></tr></thead>
              <tbody>
                {ws.tasks.map((t) => (
                  <tr key={t.id}>
                    <td className="font-medium">{t.title}</td>
                    <td><StatusBadge tone={t.priority === "urgent" ? "critical" : t.priority === "high" ? "warning" : "primary"}>{t.priority}</StatusBadge></td>
                    <td className="text-muted-foreground">{users.find(u => u.id === t.assignee_user_id)?.name || "—"}</td>
                    <td>{t.due_date ? formatDate(t.due_date) : "—"}</td>
                    <td><StatusBadge tone={t.status === "done" ? "success" : t.status === "blocked" ? "critical" : "primary"}>{t.status}</StatusBadge></td>
                  </tr>
                ))}
                {ws.tasks.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-sm text-muted-foreground">No tasks yet.</td></tr>}
              </tbody>
            </table>
          </div>
          {newTask && (
            <TaskModal
              task={newTask}
              users={users}
              onClose={() => setNewTask(null)}
              onSave={async () => {
                if (!newTask.title) return toast.error("Title required");
                await api.post("/tasks", newTask);
                toast.success("Task created");
                setNewTask(null);
                load();
              }}
              setTask={setNewTask}
            />
          )}
        </div>
      )}

      {/* AI INSIGHTS */}
      {tab === "ai" && (
        <div data-testid="workspace-ai-tab">
          <StructuredInsightsPanel clientId={id} />
          <div className="mt-4 text-right">
            <Link to="/ai-insights" className="inline-flex items-center gap-1 text-xs text-[hsl(var(--ai))] hover:underline font-medium">Org-wide AI Insights →</Link>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Metric({ label, value, sub, tone }) {
  const toneCls = tone === "critical" ? "text-[hsl(var(--critical))]"
    : tone === "warning" ? "text-[hsl(38_92%_30%)]"
    : tone === "success" ? "text-[hsl(var(--success))]" : "";
  return (
    <div className="bg-card border border-border rounded-sm p-3">
      <div className="label-mono">{label}</div>
      <div className={`font-heading text-lg font-light tabular mt-1 ${toneCls}`}>{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

function Toggle({ checked, onChange, label, tip, testid }) {
  return (
    <label
      title={tip}
      className={`h-9 px-3 inline-flex items-center gap-1.5 text-xs border rounded-sm cursor-pointer select-none ${
        checked ? "border-primary bg-primary/5 text-primary font-medium" : "border-border bg-card hover:bg-secondary"
      }`}
    >
      <input
        type="checkbox"
        className="sr-only"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
      />
      <span className={`w-3.5 h-3.5 inline-flex items-center justify-center border rounded-sm ${checked ? "bg-primary border-primary text-white" : "border-border"}`}>
        {checked && <span className="text-[10px] leading-none">✓</span>}
      </span>
      {label}
    </label>
  );
}

function TaskModal({ task, users, onClose, onSave, setTask }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card border border-border rounded-sm p-6 max-w-lg w-full" onClick={(e) => e.stopPropagation()} data-testid="workspace-task-modal">
        <div className="font-heading text-lg font-medium mb-4">New Task</div>
        <div className="space-y-3">
          <input placeholder="Title" value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} className="w-full h-10 px-3 border border-border rounded-sm text-sm" data-testid="ws-task-title" />
          <textarea placeholder="Description" value={task.description} onChange={(e) => setTask({ ...task, description: e.target.value })} className="w-full h-20 px-3 py-2 border border-border rounded-sm text-sm" />
          <div className="grid grid-cols-2 gap-2">
            <select value={task.priority} onChange={(e) => setTask({ ...task, priority: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm">
              <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="urgent">Urgent</option>
            </select>
            <input type="date" value={task.due_date || ""} onChange={(e) => setTask({ ...task, due_date: e.target.value })} className="h-10 px-3 border border-border rounded-sm text-sm" />
          </div>
          <select value={task.assignee_user_id || ""} onChange={(e) => setTask({ ...task, assignee_user_id: e.target.value || null })} className="w-full h-10 px-3 border border-border rounded-sm text-sm">
            <option value="">Unassigned</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
          </select>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={onSave} data-testid="ws-task-save" className="flex-1 h-10 bg-primary text-primary-foreground text-sm font-medium rounded-sm">Create</button>
          <button onClick={onClose} className="flex-1 h-10 border border-border text-sm rounded-sm">Cancel</button>
        </div>
      </div>
    </div>
  );
}
